<template>
  <v-container>
    <!-- ─────────────── AMOUNTS SUMMARY (by label) ─────────────── -->
    <v-row class="mb-4" dense>
      <v-col cols="12" sm="3">
        <v-card outlined>
          <v-card-text class="py-3">
            <div class="text-subtitle-2 grey--text text--darken-1">All Labels</div>
            <div class="text-h5 font-weight-bold">{{ formatMoney(grandTotal) }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-for="item in labelTotals" :key="item.label" cols="12" sm="3">
        <v-card outlined>
          <v-card-text class="py-3">
            <div class="text-subtitle-2 grey--text text--darken-1">{{ prettyLabel(item.label) }}</div>
            <div class="text-h6 font-weight-bold">{{ formatMoney(item.total) }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ─────────────── FILTERS ─────────────── -->
    <v-row class="mb-4" dense>
      <!-- Store(s) -->
      <v-col cols="12" sm="6" md="3">
        <v-select v-model="selectedStoreIds" :items="branchList" item-text="name" item-value="id"
          label="Filter by Store(s)" multiple clearable dense outlined />
      </v-col>

      <!-- Stock(s) -->
      <v-col cols="12" sm="6" md="4">
        <v-autocomplete :loading="stockLoading" v-model="selectedStockCodes" :items="stockList"
          :search-input.sync="stockSearch" no-filter item-text="product_name" item-value="stock_code"
          label="Filter by Stock(s)" multiple clearable dense outlined>
          <template #item="{ item }">
            <div>{{ item.product_name }} ({{ item.stock_code }})</div>
          </template>
          <template #selection="{ item }">
            <div>{{ item.product_name }} ({{ item.stock_code }})</div>
          </template>
        </v-autocomplete>
      </v-col>

      <!-- Label(s) -->
      <v-col cols="12" sm="6" md="3">
        <v-select v-model="selectedGroupLabels" :items="groupLabels" item-text="name" item-value="id"
          label="Filter by Label(s)" multiple clearable dense outlined />
      </v-col>

      <!-- Start Date -->
      <v-col cols="12" sm="6" md="3">
        <v-menu v-model="startMenu" :close-on-content-click="false" transition="scale-transition" offset-y
          min-width="auto">
          <template #activator="{ on, attrs }">
            <v-text-field v-model="startDate" label="Start Date" readonly v-bind="attrs" v-on="on" dense outlined
              clearable />
          </template>
          <v-date-picker v-model="startDate" @input="startMenu = false" :max="endDate" />
        </v-menu>
      </v-col>

      <!-- End Date -->
      <v-col cols="12" sm="6" md="3">
        <v-menu v-model="endMenu" :close-on-content-click="false" transition="scale-transition" offset-y
          min-width="auto">
          <template #activator="{ on, attrs }">
            <v-text-field v-model="endDate" label="End Date" readonly v-bind="attrs" v-on="on" dense outlined
              clearable />
          </template>
          <v-date-picker v-model="endDate" @input="endMenu = false" :min="startDate" />
        </v-menu>
      </v-col>
    </v-row>

    <!-- ─────────────── AMOUNTS TABLE ─────────────── -->
    <v-data-table :server-items-length="serverItemsLength" :items-per-page.sync="itemsPerPage" :page.sync="page" dense
      class="elevation-1" :headers="headers" :items="displayRows" :loading="loading" loading-text="Loading amounts..."
      :footer-props="{ 'items-per-page-options': [10, 20, 40, 80, 160, 10000] }">
      <template #item.pricePerUnit="{ item }">
        <span>{{ item.pricePerUnit != null ? formatMoney(item.pricePerUnit) : '—' }}</span>
      </template>

      <template #item.discount="{ item }">
        <span>{{ item.discount != null ? formatMoney(item.discount) : '—' }}</span>
      </template>

      <template #item.amount="{ item }">
        <span class="font-weight-medium">{{ formatMoney(item.amount) }}</span>
      </template>

      <template #body.append>
        <tr>
          <td colspan="9" class="text-right font-weight-bold">Grand Total</td>
          <td class="text-no-wrap font-weight-bold">{{ formatMoney(grandTotal) }}</td>
        </tr>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
/* eslint-disable no-console */
import axios from '@/extra-functions/axios.extra'
import { ErrorPopup, useDebounce, LoadingPopup } from '@/lib/utils'
import { mapState } from 'vuex'

const API_PATH = '/inventory-breakdown'

export default {
  name: 'IndexAmounts',

  data() {
    return {
      // Filters
      selectedStoreIds: [],
      selectedStockCodes: [],
      selectedGroupLabels: [],
      startDate: null,
      endDate: null,

      // Options
      stockList: [],
      groupLabels: [],

      // UI
      stockSearch: null,
      stockLoading: false,
      loading: false,
      startMenu: false,
      endMenu: false,
      stockDebounce: useDebounce(),

      // Table
      rows: [],
      headers: [
        { text: 'Label', value: 'groupLabel' },
        { text: 'Ref #', value: 'receiptNo', sortable: false },
        { text: 'Store', value: 'storeName' },
        { text: 'Stock Code', value: 'stockCode' },
        { text: 'Item', value: 'stockName' },
        { text: 'Unit', value: 'stockUnit' },
        { text: 'Qty', value: 'quantity' },
        { text: 'Type', value: 'stockBrandTypeName' },
        { text: 'Price/Unit', value: 'pricePerUnit' },
        { text: 'Discount', value: 'discount' },
        { text: 'Amount', value: 'amount' },
        { text: 'Timestamp', value: 'date' },
      ],
      page: 1,
      serverItemsLength: -1,
      itemsPerPage: 10,
    }
  },

  computed: {
    ...mapState({
      branchList: state => state.branchLocation.list,
      errors: state => state.auth.errors,
      loggingIn: state => state.auth.loggingIn,
      sessionExpired: state => state.auth.sessionExpired,
    }),

    displayRows() { return this.rows },

    // Totals by label for the summary cards
    labelTotals() {
      const map = new Map()
      for (const r of this.rows) {
        const key = r.groupLabel
        map.set(key, (map.get(key) || 0) + (r.amount || 0))
      }
      // Sort by total desc
      return Array.from(map.entries())
        .map(([label, total]) => ({ label, total }))
        .sort((a, b) => b.total - a.total)
    },

    grandTotal() {
      return this.rows.reduce((s, r) => s + (r.amount || 0), 0)
    },
  },

  created() {
    this.fetchRows()
    this.fetchStockList()
    this.fetchGroupLabels()
  },

  watch: {
    page() { this.fetchRows() },
    itemsPerPage() { this.fetchRows() },

    selectedStoreIds() { this.onFiltersChanged() },
    selectedStockCodes() { this.onFiltersChanged() },
    selectedGroupLabels() { this.onFiltersChanged() },
    startDate() { this.onFiltersChanged() },
    endDate() { this.onFiltersChanged() },

    stockSearch() { this.stockDebounce(() => this.fetchStockList()) },
  },

  methods: {
    onFiltersChanged() {
      this.page = 1
      this.fetchRows()
    },

    formatMoney(v) {
      const n = Number(v || 0)
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    prettyLabel(s) {
      // humanize known labels; fallback to capitalized
      const map = {
        sales: 'Sales',
        purchases: 'Purchases',
        'store-receives': 'Store Receives',
        'store-transfers': 'Store Transfers',
        lost: 'Lost',
        collectibles: 'Collectibles',
        'sale-promotions': 'Sale Promotions',
        'restore-points': 'Restore Points',
      }
      if (map[s]) return map[s]
      return String(s || '').replace(/(^|-)\w/g, m => m.toUpperCase())
    },

    // snake_case -> camelCase + compute amount
    mapRow(r) {
      const quantity = Number(r.quantity ?? 0)
      const pricePerUnit = r.price_per_unit != null ? Number(r.price_per_unit) : null
      const discount = r.discount != null ? Number(r.discount) : null
      const price = Number(pricePerUnit || 0)
      const disc = Number(discount || 0)
      const amount = (price * quantity) - disc

      return {
        groupLabel: r.group_label,
        receiptNo: r.receipt_no,
        storeName: r.store_name,
        stockCode: r.stock_code,
        stockName: r.stock_name,
        stockUnit: r.stock_unit,
        stockBrandTypeName: r.stock_brand_type_name,
        quantity,
        pricePerUnit,
        discount,
        amount,
        date: r.date || r.created_at,
      }
    },

    async fetchRows() {
      this.loading = true
      try {
        LoadingPopup.fire('Loading amounts...')
        const { data } = await axios(this.$store.state.auth.token).get(API_PATH, {
          params: {
            perPage: this.itemsPerPage,
            page: this.page,
            stores: this.selectedStoreIds,
            stockCodes: this.selectedStockCodes,
            groupLabels: this.selectedGroupLabels,
            startDate: this.startDate,
            endDate: this.endDate,
          },
        })
        LoadingPopup.close()

        const rawRows = Array.isArray(data.data) ? data.data : []
        this.rows = rawRows.map(this.mapRow)
        this.serverItemsLength = Number(data.total ?? rawRows.length)
      } catch (e) {
        ErrorPopup.fire('Failed to load amounts.')
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    async fetchGroupLabels() {
      try {
        const { data } = await axios(this.$store.state.auth.token).get('/inventory-breakdown/group-labels')
        this.groupLabels = data // expects [{ id, name }]
      } catch (e) {
        ErrorPopup.fire('Failed to fetch group labels.')
        console.error(e)
      }
    },

    async fetchStockList() {
      this.stockLoading = true
      try {
        const { data } = await axios(this.$store.state.auth.token).get('/stocks/stock-codes', {
          params: { search: this.stockSearch },
        })
        this.stockList = data
      } catch (e) {
        ErrorPopup.fire('Failed to fetch stocks.')
        console.error(e)
      } finally {
        this.stockLoading = false
      }
    },
  },
}
</script>

<style scoped>
/* light spacing tweaks for dense table */
</style>
